import { useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import TransformationSelector from './TransformationSelector';
import type {
  FieldMapping,
  TransformationType,
  SourceSystem,
  TransformationParams,
} from '@types';

interface FieldMapperProps {
  mappings: Record<string, FieldMapping>;
  sourceSystem: SourceSystem;
  onChange: (mappings: Record<string, FieldMapping>) => void;
}

// Common payroll fields by source system
const sourceFields: Record<SourceSystem, string[]> = {
  QUICKBOOKS: [
    'employee_id',
    'first_name',
    'last_name',
    'email',
    'hire_date',
    'department',
    'position',
    'salary',
    'pay_rate',
    'status',
  ],
  ADP: [
    'file_number',
    'associate_id',
    'name_first',
    'name_last',
    'email_address',
    'hire_date',
    'dept_code',
    'job_title',
    'annual_salary',
    'hourly_rate',
    'employment_status',
  ],
  PAYCHEX: [
    'emp_no',
    'fname',
    'lname',
    'email',
    'start_date',
    'department_id',
    'job_code',
    'salary_amount',
    'wage_rate',
    'active_status',
  ],
  GUSTO: [
    'employee_number',
    'first_name',
    'last_name',
    'work_email',
    'date_of_hire',
    'department_name',
    'job_title',
    'annual_compensation',
    'hourly_compensation',
    'status',
  ],
  WORKDAY: [
    'worker_id',
    'legal_first_name',
    'legal_last_name',
    'business_email',
    'hire_date',
    'cost_center',
    'position_title',
    'base_salary',
    'hourly_rate',
    'worker_status',
  ],
};

const targetFields = [
  'employee_id',
  'first_name',
  'last_name',
  'email',
  'hire_date',
  'department',
  'job_title',
  'salary',
  'hourly_rate',
  'status',
  'phone',
  'address',
  'city',
  'state',
  'zip_code',
  'ssn',
  'birth_date',
  'termination_date',
];

export default function FieldMapper({
  mappings,
  sourceSystem,
  onChange,
}: FieldMapperProps) {
  const [expandedMapping, setExpandedMapping] = useState<string | null>(null);

  const mappingArray = Object.entries(mappings).map(([key, value]) => ({
    key,
    ...value,
  }));

  const addMapping = () => {
    const newKey = `mapping_${Date.now()}`;
    const newMappings = {
      ...mappings,
      [newKey]: {
        source_field: '',
        target_field: '',
        transformation_rule: 'none' as TransformationType,
        transformation_params: {},
      },
    };
    onChange(newMappings);
    setExpandedMapping(newKey);
  };

  const removeMapping = (key: string) => {
    const newMappings = { ...mappings };
    delete newMappings[key];
    onChange(newMappings);
    if (expandedMapping === key) {
      setExpandedMapping(null);
    }
  };

  const updateMapping = (
    key: string,
    field: keyof FieldMapping,
    value: string
  ) => {
    const newMappings: Record<string, FieldMapping> = {
      ...mappings,
      [key]: {
        ...mappings[key],
        [field]: value,
      } as FieldMapping,
    };
    onChange(newMappings);
  };

  const updateTransformation = (
    key: string,
    type: TransformationType,
    params: TransformationParams
  ) => {
    const newMappings: Record<string, FieldMapping> = {
      ...mappings,
      [key]: {
        ...mappings[key],
        transformation_rule: type,
        transformation_params: params,
      } as FieldMapping,
    };
    onChange(newMappings);
  };

  const toggleExpanded = (key: string) => {
    setExpandedMapping(expandedMapping === key ? null : key);
  };

  const availableSourceFields = sourceFields[sourceSystem] || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Field Mappings</h3>
        <button
          type="button"
          onClick={addMapping}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add Mapping
        </button>
      </div>

      {/* Mappings List */}
      {mappingArray.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">No field mappings defined yet</p>
          <button
            type="button"
            onClick={addMapping}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Your First Mapping
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {mappingArray.map(mapping => (
            <div
              key={mapping.key}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
            >
              {/* Mapping Row */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                  {/* Source Field */}
                  <div className="md:col-span-5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Source Field
                    </label>
                    <select
                      value={mapping.source_field}
                      onChange={e =>
                        updateMapping(
                          mapping.key,
                          'source_field',
                          e.target.value
                        )
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Select source field...</option>
                      {availableSourceFields.map(field => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Arrow */}
                  <div className="md:col-span-1 flex items-center justify-center pt-6">
                    <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                  </div>

                  {/* Target Field */}
                  <div className="md:col-span-5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Target Field
                    </label>
                    <select
                      value={mapping.target_field}
                      onChange={e =>
                        updateMapping(
                          mapping.key,
                          'target_field',
                          e.target.value
                        )
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Select target field...</option>
                      {targetFields.map(field => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-1 flex items-center justify-end gap-1 pt-6">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(mapping.key)}
                      className="p-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Configure transformation"
                    >
                      {mapping.transformation_rule &&
                      mapping.transformation_rule !== 'none'
                        ? '⚡'
                        : '⚙️'}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMapping(mapping.key)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Remove mapping"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Transformation Configuration (Expandable) */}
                {expandedMapping === mapping.key && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <TransformationSelector
                        transformationType={
                          mapping.transformation_rule || 'none'
                        }
                        transformationParams={
                          mapping.transformation_params || {}
                        }
                        onChange={(type, params) =>
                          updateTransformation(mapping.key, type, params)
                        }
                        sourceValue={
                          mapping.source_field
                            ? `Sample ${mapping.source_field}`
                            : ''
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Transformation Badge */}
              {mapping.transformation_rule &&
                mapping.transformation_rule !== 'none' && (
                  <div className="px-4 pb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      ⚡ {mapping.transformation_rule.replace('_', ' ')}
                    </span>
                  </div>
                )}
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {mappingArray.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Mapping Summary
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  <strong>{mappingArray.length}</strong> field
                  {mappingArray.length !== 1 ? 's' : ''} mapped
                  {mappingArray.filter(
                    m =>
                      m.transformation_rule && m.transformation_rule !== 'none'
                  ).length > 0 && (
                    <>
                      {' '}
                      |{' '}
                      <strong>
                        {
                          mappingArray.filter(
                            m =>
                              m.transformation_rule &&
                              m.transformation_rule !== 'none'
                          ).length
                        }
                      </strong>{' '}
                      with transformations
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
